<template>
  <div>
    <form>
      <Fluid>
        <MnRow>
          <MnColumn>
            <FormInputGroup label="Transaction Title"
                            inputId="title-input"
                            :state="formState.state.$v.title.$error"
                            :errorMessage="formState.state.$v.title.$errors[0]?.$message" required>
              <template #default="{ inputId, state, updateState }">
                <PvInputText :id="inputId"
                             v-model="formState.state.$v.title.$model"
                             :invalid="state"
                             @input="updateState($event)" />
              </template>
            </FormInputGroup>            
          </MnColumn>
        </MnRow>
        <MnRow>
          <MnColumn>
            <FormSelectGroup label="Transaction Type"
                             inputId="type-input"
                             :state="formState.state.$v.transactionTypeId.$error"
                             :errorMessage="formState.state.$v.transactionTypeId.$errors[0]?.$message" required>
              <template #default="{ inputId, state, updateState, ariaLabelledby }">
                <PvSelect :inputId="inputId"
                          :options="transactionTypes"
                          v-model="formState.state.$v.transactionTypeId.$model"
                          :aria-labelledby="ariaLabelledby"
                          :invalid="state"
                          @change="setTransactionType($event)"
                          optionLabel="textValue1"
                          optionValue="attributeId"
                          @input="updateState($event)" />
              </template>
            </FormSelectGroup>
          </MnColumn>
        </MnRow>
        <MnRow>
          <MnColumn cols="6">
            <FormInputGroup label="Proposed Completion Date"
                            inputId="proposed-input"
                            hint="MM/DD/YYYY"
                            :state="formState.state.$v.proposedCompletionDate.$error"
                            :errorMessage="formState.state.$v.proposedCompletionDate.$errors[0]?.$message" required>
              <template #default="{ inputId, state, updateState, ariaDescribedby }">
                <PvDatePicker :inputId="inputId"
                              v-model="formState.state.$v.proposedCompletionDate.$model"
                              :aria-describedby="ariaDescribedby"
                              :showIcon="true"
                              :showButtonBar="true"
                              :showOnFocus="true"
                              :invalid="state"
                              :showTime="false"
                              @input="updateState($event)" />          
              </template>
            </FormInputGroup>
          </MnColumn>
          <MnColumn v-if="showFirstAnticipatedDate" cols="6">
            <FormInputGroup label="Date Transaction First Anticipated"
                            inputId="anticipated-input"
                            hint="MM/DD/YYYY"
                            :state="formState.state.$v.anticipatedDate.$error"
                            :errorMessage="formState.state.$v.anticipatedDate.$errors[0]?.$message" required>
              <template #default="{ inputId, state, updateState, ariaDescribedBy }">
                <PvDatePicker :inputId="inputId"
                              v-model="formState.state.$v.anticipatedDate.$model"
                              :aria-describedby="ariaDescribedBy" 
                              :showIcon="true"
                              :showButtonBar="true"
                              :showOnFocus="true"
                              :invalid="state"
                              :showTime="false"
                              @input="updateState($event)" />          
              </template>
            </FormInputGroup>
          </MnColumn>
        </MnRow>
        <MnRow>
          <MnColumn>
            <FormInputGroup label="Transaction Description"
                            inputId="description-input"
                            :state="formState.state.$v.description.$error"
                            :errorMessage="formState.state.$v.description.$errors[0]?.$message" required>
              <template #default="{ inputId, state, updateState }">
                <PvInputText :id="inputId"
                             v-model="formState.state.$v.description.$model"
                             :invalid="state"
                             @input="updateState($event)" />
              </template>
            </FormInputGroup>
          </MnColumn>
        </MnRow>
      </Fluid>
    </form>
    <ErrorMessage id="error-msg" :message="formState.state.errorMessage" />
  </div>
</template>

<script setup>
import { onMounted, nextTick, computed } from 'vue';
import MnRow from '@/components/common/MnRow.vue';
import MnColumn from '@/components/common/MnColumn.vue';
import FormInputGroup from '@/components/common/FormInputGroup.vue';
import FormSelectGroup from '@/components/common/FormSelectGroup.vue';
import ErrorMessage from '@/components/common/ErrorMessage.vue';
import Fluid from 'primevue/fluid';
import { TxTransactionModel } from '../models/HceTransactionModel';
import { useFormHelper } from '@/composables/formHelper';
import { useAppStore } from '@/stores/appStore';
import { ATTRIBUTE_TYPES } from '@/utils/constants';

const formState = useFormHelper(new TxTransactionModel(), TxTransactionModel.getValidationRules());
const appStore = useAppStore();
const transactionTypes = computed(() => appStore.getAttributeValues(ATTRIBUTE_TYPES.HceTransaction));

const showFirstAnticipatedDate = computed(() => {
  return formState.state.data.includeAnticipatedDate;
});

// Handles the form submission logic, including validation and saving data
const onSubmit = async () => {
  formState.state.$v.$touch();
  if (formState.state.$v.$invalid) {
      nextTick(() => formState.focusFirstInvalidInput());
  } else {
      await formState.save(() => formState.state.data.save());
    if (!formState.state.hasError) {
      emit('data-saved', formState.state.data.id);
    } else {
      formState.focusErrorMessage('error-msg');
    }      
  }
};

// Sets the transaction type based on the selected value
const setTransactionType = (event) => {
  const orgType = appStore.getAttributeTextValue1ById(event.value);
  formState.state.data.transactionType = orgType;
};

// Lifecycle hook that runs when the component is mounted
onMounted(async () => {
 nextTick(() => { formState.setFocus('title-input'); });
});

const emit = defineEmits(['data-saved']);

defineExpose({ onSubmit });
</script>